import usePrivateAxios from "@/common/api/usePrivateAxios";
import { intervalToSeconds } from "@/common/utils/intervalToSeconds";
import { unFormatCurrency, unFormatUnit } from "@/common/utils/unFormatters";
import { CompanyBranchValidationSchema } from "@/features/privateCompanies/schemas/createBranch.schema";
import { transformNewProductCategoryRestrictions } from "@/features/privateCompanies/utils/productCategoryRestrictionsTransformers";
import { useMutation } from "@tanstack/react-query";
import { transformNewProductCategoryGrantedCredit } from "../services/companyBranchTransformer";
import { ControlledByType } from "../types/companyGovernmentAllocation.type";

interface UseCreateCompanyBranch {
  companyId?: string,
  controlledBy?: ControlledByType,
  onSuccess: () => void,
  onError: (error: any) => void,
}

const useCreateCompanyBranch = ({
  companyId,
  controlledBy,
  onSuccess,
  onError
}: UseCreateCompanyBranch) => {
  const privateAxios = usePrivateAxios();
  return useMutation({
    mutationKey: ['createCompanyBranch'],
    mutationFn: async ({ form, file }: { form: CompanyBranchValidationSchema, file?: File }) => {
      const minimum_kilometers_usage_between_consumptions = form.minimum_kilometers_usage_between_consumptions
        ? (unFormatUnit(form.minimum_kilometers_usage_between_consumptions, 'Km') || '')
        : '';
      const maximum_kilometers_usage_between_consumptions = form.maximum_kilometers_usage_between_consumptions
        ? (unFormatUnit(form.maximum_kilometers_usage_between_consumptions, 'Km') || '')
        : '';
      const minimum_seconds_usage_between_consumptions = intervalToSeconds({
        days: Number(form.minimum_seconds_usage_between_consumptions?.days || 0),
        hours: Number(form.minimum_seconds_usage_between_consumptions?.hours || 0),
        minutes: Number(form.minimum_seconds_usage_between_consumptions?.minutes || 0),
        seconds: Number(form.minimum_seconds_usage_between_consumptions?.seconds || 0),
      });
      const maximum_seconds_usage_between_consumptions = intervalToSeconds({
        days: Number(form.maximum_seconds_usage_between_consumptions?.days || 0),
        hours: Number(form.maximum_seconds_usage_between_consumptions?.hours || 0),
        minutes: Number(form.maximum_seconds_usage_between_consumptions?.minutes || 0),
        seconds: Number(form.maximum_seconds_usage_between_consumptions?.seconds || 0),
      });
      const minimum_seconds_interval_between_consumptions = intervalToSeconds({
        days: Number(form.minimum_seconds_interval_between_consumptions?.days || 0),
        hours: Number(form.minimum_seconds_interval_between_consumptions?.hours || 0),
        minutes: Number(form.minimum_seconds_interval_between_consumptions?.minutes || 0),
        seconds: Number(form.minimum_seconds_interval_between_consumptions?.seconds || 0),
      });

      const payload = {
        company_id: companyId,
        display_name: form.display_name,
        parent_id: form.parent?.value,
        should_have_own_bill_when_dynamic: form.should_have_own_bill_when_dynamic,
        master_client_user_id: form.master_client_user_id?.value,
        general_information_attributes: form.general_information?.willUseOwnData
          ? {
            ...form.general_information,
            address_attributes: {
              ...form.general_information?.address_attributes,
              city_id: form.general_information?.address_attributes?.city?.id,
            },
            company_logo: file || undefined,
          } : undefined,
        restriction_attributes: {
          establishment_block_mode:
            form.enableEstablishmentRestriction
              ? form.establishment_block_mode?.value
              : undefined,
          allowed_or_blocked_establishments_attributes:
            form.enableEstablishmentRestriction
              ? form.allowed_or_blocked_establishments_attributes?.map((establishment) => ({ establishment_branch_id: establishment.value }))
              : undefined,
          period_restrictions_attributes: form.enablePeriodRestriction
            ? form.period_restrictions_attributes?.map((periodRestriction) => {
              return {
                id: periodRestriction.id,
                week_day: periodRestriction.week_day,
                intervals_attributes: [
                  ...(() => {
                    const newIntervals = [] as { start_time?: string | null, end_time?: string | null; }[];

                    if (periodRestriction.blocked) {
                      return [];
                    }

                    if (periodRestriction.intervals_attributes?.some((interval) => interval.start_time?.value === 'all_day')) {
                      newIntervals.push({ start_time: '00:00', end_time: '23:59' });
                      return newIntervals;
                    }

                    periodRestriction?.intervals_attributes?.forEach((interval) => {
                      newIntervals.push({ start_time: interval.start_time?.value, end_time: interval.end_time?.value });
                    });
                    return newIntervals;
                  })(),
                ]
              };
            })
            : undefined,
          minimum_kilometers_usage_between_consumptions,
          maximum_kilometers_usage_between_consumptions,
          minimum_seconds_usage_between_consumptions:
            minimum_seconds_usage_between_consumptions > 0
              ? minimum_seconds_usage_between_consumptions
              : '',
          minimum_seconds_interval_between_consumptions:
            minimum_seconds_interval_between_consumptions > 0
              ? minimum_seconds_interval_between_consumptions
              : '',
          maximum_seconds_usage_between_consumptions:
            maximum_seconds_usage_between_consumptions > 0
              ? maximum_seconds_usage_between_consumptions
              : '',
          product_category_restrictions_attributes:
            form.product_category_restrictions_attributes?.map(transformNewProductCategoryRestrictions) || [],
        },
        granted_credit: unFormatCurrency(form.granted_credit),
        product_category_granted_credits_attributes: form.enableCreditConfiguration
          ? form.product_category_granted_credits_attributes.map(
              (category) => transformNewProductCategoryGrantedCredit(category, controlledBy)
            )
          : null,
        fiscal_document_information_attributes: {
          ...form.fiscal_document_information_attributes,
          fiscal_document_kind: form.fiscal_document_information_attributes?.fiscal_document_kind?.value || undefined,
        },
        name_on_card: form.name_on_card ? form.name_on_card.trim() : undefined,
        additional_documents_attachments_attributes: form.additional_documents_attachments_attributes?.map((attachment) => ({
          blob_id: attachment.id,
        })),
      };

      return privateAxios.postForm(`/client/company_branches`, { company_branch: payload });
    },
    onSuccess,
    onError,
  });
};

export default useCreateCompanyBranch;
